'use client';

import styled from 'styled-components';

import { ReactNode, useCallback, useContext, useMemo } from 'react';

import { Card } from '@/components/atoms/Card/Card';
import { MultiSelectBox } from '@/components/atoms/MultiSelectBox/MultiSelectBox';
import { RadioGroup } from '@/components/atoms/RadioGroup/RadioGroup';
import { Select } from '@/components/atoms/Select/Select';
import { Title } from '@/components/atoms/Title/Title';
import { DirectoryInteractionsContext } from '@/contexts/DirectoryInteractions';

export interface CareFiltersProps {
  disabled?: boolean;
  selectedValues: any;
  allValues: any;
  toggleValues: any;
}

export interface CareFilterChoiceProps {
  disabled?: boolean;
  selectedValue: any;
  allValues: any;
  setSelectedValue: any;
}

const mapConverter = (val: any) => {
  return JSON.stringify(Object.keys(val).filter((v) => val[v]));
};

export const CareProvidedFilters = ({
  disabled,
  selectedValues,
  allValues,
  toggleValues,
}: CareFiltersProps) => (
  <MultiSelectBox
    variant='default'
    value={JSON.stringify(selectedValues)}
    options={allValues.map((n: string) => ({ label: n, value: n }))}
    onChange={(v: any) => toggleValues(JSON.parse(v))}
    isDisabled={disabled}
    maxColumns={1}
  />
);

export const CareTypePicker = ({
  selectedValue,
  setSelectedValue,
  allValues,
}: CareFilterChoiceProps) => {
  return (
    <RadioGroup
      onChange={setSelectedValue}
      value={selectedValue}
      options={[...allValues]}
      variant='default'
    />
  );
};

export const DistanceFilters = ({
  disabled,
  allValues,
  selectedValue,
  setSelectedValue,
}: CareFilterChoiceProps) => {
  const vals = useMemo(
    () =>
      allValues.map((distance: string, i: number) => ({
        label: `${distance} miles`,
        value: distance,
      })),
    [allValues],
  );

  return (
    <RadioGroup
      isDisabled={disabled}
      onChange={setSelectedValue}
      value={selectedValue}
      options={[...vals]}
      variant='default'
      maxColumns={1}
    />
  );
};

export const HomeCareDurationFilters = ({
  disabled,
  allValues,
  selectedValue,
  setSelectedValue,
}: CareFilterChoiceProps) => {
  return (
    <RadioGroup
      onChange={setSelectedValue}
      value={selectedValue}
      options={[...allValues]}
      isDisabled={disabled}
      variant='default'
      maxColumns={1}
    />
  );
};

export const HomeCareRateFilters = ({
  disabled,
  allValues,
  selectedValues,
  toggleValues,
}: CareFiltersProps) => {
  const value = useMemo(() => mapConverter(selectedValues), [selectedValues]);

  return (
    <MultiSelectBox
      variant='default'
      value={value}
      options={[...allValues]}
      onChange={(v: any) => toggleValues(JSON.parse(v))}
      isDisabled={disabled}
      maxColumns={1}
    />
  );
};

interface InlineTextProps {
  $primary?: boolean;
  $secondary?: boolean;
  $light?: boolean;
  $disabled?: boolean;
  $transform?: string;
  $noWrap?: boolean;
}

const DirectoryInlineText = styled.span<InlineTextProps>`
  margin: 0;
  ${({ theme, $primary }) => $primary && `color: ${theme.color.primary}`};
  ${({ theme, $secondary }) =>
    $secondary && `color: ${theme.color.primary[90]}`};
  ${({ theme, $light }) => $light && `color: ${theme.color.neutral[40]}`};
  ${({ theme, $disabled }) =>
    $disabled && `color: ${theme.color.status.disabled}`};
  text-transform: ${({ $transform }) => $transform || 'initial'};
  ${({ $noWrap }) => $noWrap && 'white-space: nowrap'};
`;

interface RegulatorsRatingFiltersProps extends CareFiltersProps {
  lastSearchRegulator: any;
}

export const RegulatorsRatingFilters = ({
  allValues,
  selectedValues,
  toggleValues,
  lastSearchRegulator,
}: RegulatorsRatingFiltersProps) => {
  const value = useMemo(() => mapConverter(selectedValues), [selectedValues]);

  const weightsMap = useCallback(
    (label: string) => {
      const r = allValues[lastSearchRegulator].find(
        (rating: any) => rating.label === label,
      );

      return r?.weighting;
    },
    [allValues, lastSearchRegulator],
  );

  if (!lastSearchRegulator) {
    return null;
  }

  if (!allValues[lastSearchRegulator]) {
    return <DirectoryInlineText>Unknown regulatory body</DirectoryInlineText>;
  }

  return (
    <MultiSelectBox
      variant='default'
      value={value}
      options={[...allValues[lastSearchRegulator]].map(({ label }) => ({
        label,
        value: weightsMap(label),
      }))}
      onChange={(val: any) => toggleValues(JSON.parse(val))}
      maxColumns={1}
    />
  );
};

export const SortOrderFilter = ({
  disabled,
  careType,
  sortOrders,
  currentSortOrder,
  setCurrentSortOrder,
}: {
  disabled?: boolean;
  careType?: any;
  sortOrders?: any;
  currentSortOrder: any;
  setCurrentSortOrder: any;
}) => {
  const sortOptions = sortOrders[careType];

  return (
    <Select
      onChange={setCurrentSortOrder}
      options={[...sortOptions]}
      value={currentSortOrder}
      isDisabled={disabled}
    />
  );
};

interface WrapFilterCardProps {
  key: string;
  isShow?: boolean;
  title: string;
  isTop?: boolean;
  children: ReactNode;
}

const WrapFilterCard = ({ isShow, title, children }: WrapFilterCardProps) => {
  return isShow ? (
    <Card isPaddingSm>
      <Title isNoMargin size='sm'>
        {title}
      </Title>
      <FilterContent>{children}</FilterContent>
    </Card>
  ) : null;
};

interface CareProviderSearchSidebarProps {
  isDisabled?: boolean;
}

export const CareProviderSearchSidebar = ({
  isDisabled,
}: CareProviderSearchSidebarProps) => {
  const { filters, modifyFilter, results } = useContext(
    DirectoryInteractionsContext,
  );
  const currentCareType = useMemo(
    () => filters.careType.selectedValue,
    [filters.careType.selectedValue],
  );
  const lastSearchRegulator = results?.regulator || 'cqc';

  return (
    <CareProviderSearchSidebarWrapper>
      <WrapFilterCard key={'sorting'} isTop isShow title={'Sort results by'}>
        <SortOrderFilter
          disabled={isDisabled}
          careType={currentCareType}
          sortOrders={filters.sortOrder.allValues}
          currentSortOrder={filters.sortOrder.selectedValue}
          setCurrentSortOrder={(val: any) => modifyFilter('sortOrder', val)}
        />
      </WrapFilterCard>
      <WrapFilterCard key={'distance'} isShow title={'Distance from location'}>
        <DistanceFilters
          disabled={isDisabled}
          {...filters.distance}
          setSelectedValue={(val: any) => modifyFilter('distance', val)}
        />
      </WrapFilterCard>
      <WrapFilterCard
        key={'care'}
        isShow={currentCareType === 'careHome'}
        title={'Care provided'}
      >
        <CareProvidedFilters
          disabled={isDisabled}
          {...filters.careProvided}
          toggleValues={(val: any) => modifyFilter('careProvided', val)}
        />
      </WrapFilterCard>
      {lastSearchRegulator && lastSearchRegulator !== 'unknown' ? (
        <WrapFilterCard
          key={'ratings'}
          isShow={!isDisabled}
          title={'Regulators rating'}
        >
          <RegulatorsRatingFilters
            lastSearchRegulator={lastSearchRegulator}
            {...filters.regulatorRatings}
            toggleValues={(val: any) => modifyFilter('regulatorRatings', val)}
          />
        </WrapFilterCard>
      ) : null}
      <WrapFilterCard
        key={'rates'}
        isShow={currentCareType === 'homeCare'}
        title={'Starting hourly rate'}
      >
        <HomeCareRateFilters
          disabled={isDisabled}
          {...filters.hourlyRates}
          toggleValues={(val: any) => modifyFilter('hourlyRates', val)}
        />
      </WrapFilterCard>
      <WrapFilterCard
        key={'durations'}
        isShow={currentCareType === 'homeCare'}
        title={"I'm looking for visits of"}
      >
        <HomeCareDurationFilters
          disabled={isDisabled}
          {...filters.careVisitDuration}
          setSelectedValue={(val: any) =>
            modifyFilter('careVisitDuration', val)
          }
        />
      </WrapFilterCard>
    </CareProviderSearchSidebarWrapper>
  );
};

const FilterContent = styled.div`
  padding: ${(props) => props.theme.spacing[16]};
`;

const CareProviderSearchSidebarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[16]};
  width: 240px;
  padding-top: ${({ theme }) => theme.spacing[16]};

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export default CareProviderSearchSidebar;
